import { toast } from '@/components/toast';
import { audiosDns, projectDns, projectsBucket } from '@/environment';
import { HttpStatus } from '@/errors/http-status';
import { useAnalytics } from '@/services/analytics';
import { uploadAudio } from '@/services/api';
import { logger } from '@/services/logger';
import { ERROR_KINDS, moderationError } from '@/state/layout/moderation';
import { formatS3Url } from '@/utils/s3';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { useSetRecoilState } from 'recoil';

export interface AudioData {
    data: ArrayBuffer | string | null;
    duration: number;
    listenUrl?: string;
    name?: string;
    wordLevelTimingUrl?: string;
}

export const processSuccessfulUpload = (url: string, duration: number, resultUrl?: string) => {
    const urlData = url.split('/') || [];
    const name = urlData[urlData.length - 1];

    const isProjectBucket = resultUrl?.includes(projectsBucket);
    const listenUrl = formatS3Url(isProjectBucket ? projectDns : audiosDns, url);

    return { data: url, duration, listenUrl, name };
};

export const useUploadAudio = (onUploadComplete?: (audioData: AudioData) => void) => {
    const setModerationError = useSetRecoilState(moderationError);
    const mixpanel = useAnalytics();

    return useMutation(uploadAudio, {
        retry(retryCount, error) {
            const errorUnhandled = !error?.response?.data?.kind;
            return errorUnhandled && retryCount < 1;
        },
        onSuccess({ url, duration, moderation_metadata: moderationMetadata }, { resultUrl }) {
            if (onUploadComplete) {
                const { data, listenUrl, name } = processSuccessfulUpload(url, duration, resultUrl);
                onUploadComplete({ data, duration, listenUrl, name });
            }

            mixpanel?.track('upload-audio', { event: 'success', duration: duration });
            if (moderationMetadata) {
                mixpanel?.track('audio-moderation', { ...moderationMetadata });
            }
        },
        onError: (
            error: AxiosError<{
                kind: string;
                description?: string;
                details?: { terms: string[]; metadata: Record<string, any> };
            }>
        ) => {
            const kind = error?.response?.data?.kind;
            mixpanel?.track('upload-audio', { kind, event: 'error' });

            if (error.code === 'ERR_CANCELED') {
                return;
            }
            if (kind === ERROR_KINDS.AudioModerationError) {
                setModerationError({
                    kind: ERROR_KINDS.AudioModerationError,
                    extraData: error?.response?.data?.details?.terms,
                });
                mixpanel?.track('audio-moderation', { ...error?.response?.data?.details?.metadata });
            } else if (kind === ERROR_KINDS.UnsupportedMimeTypeError) {
                toast.info({ title: 'The file uploaded is not supported' });
                logger.warn('Unsupported MIME type audio uploaded', { error });
            } else if (kind === ERROR_KINDS.ValidationError) {
                toast.error({ title: 'The file uploaded did not pass our validation' });
                logger.warn('Audio validation failed', { error });
            } else if (error.response?.status === HttpStatus.PAYLOAD_TOO_LARGE) {
                toast.info({ title: 'Audio file should be up to 5 minutes long', toastId: `upload-audio-error` });
            } else {
                toast.error({ title: 'Audio upload failed. Please try again' });
                logger.error('Audio upload failed. Please try again', { error });
            }
        },
    });
};
